import type { GA4AnalyticsEvent } from '@/domain/shared/types'
import { useIntl } from '@/intl'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { getFiltersSelector, getPricesSelector } from '@/scopes/prices/components/selectors'
import { FiltersInPricesValues, Option } from '@/scopes/prices/domain/filters/types'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import {
  DropdownMultiselect,
  useEditableDropdownMultiselect,
} from '@/scopes/search/search/pages/SearchPage/components/Filters/DropdownMultiselect'

import {
  FormField,
  FormFieldControl,
  FormFieldLabel,
} from '@/scopes/search/search/pages/SearchPage/components/Filters/FormField'
import { IconGlyph, space } from '@sunwebgroup/ui-components'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'

const FocusPadding = styled.div`
  padding: ${space('primitives_space_12')};
`

interface MultiSelectPricesFilterProps {
  label: string
  placeholder: string
  confirmButton: string
  icon: IconGlyph
  analyticsEventBuilder: (selection: string | string[] | undefined, options: Option[]) => GA4AnalyticsEvent
  options: Option[]
  selectedValues: string[]
  setSelection: (selected: FiltersInPricesValues) => void
}

function MultiSelectPricesFilter({
  label,
  placeholder,
  confirmButton,
  analyticsEventBuilder,
  options,
  selectedValues,
  setSelection,
}: MultiSelectPricesFilterProps) {
  const intl = useIntl()
  const { isEmpty: isEmptyResponse } = usePricesSelector(getFiltersSelector)
  const { loading: isFetching } = usePricesSelector(getPricesSelector)
  const { sendEvent } = useAnalytics()
  const { isTablet } = useBreakpoints()

  const disabled = isFetching || isEmptyResponse

  const doNotRender
    = (isEmpty(options) && !isEmptyResponse) || (!isTablet && !isEmptyResponse && options.length <= 1)

  const handleValueChange = React.useCallback((newSelectedValues: string[]) => {
    setSelection(newSelectedValues)
  }, [])

  const handleEditFinish = React.useCallback(
    (newSelectedValues: string[]) => {
      sendEvent(analyticsEventBuilder(newSelectedValues, options))
    },
    [sendEvent, options],
  )

  const multiselectDropdownProps = useEditableDropdownMultiselect({
    value: selectedValues,
    onValueChange: handleValueChange,
    onEditFinish: handleEditFinish,
  })

  if (doNotRender)
    return null

  return (
    <FormField as="div">
      <FormFieldLabel>{label}</FormFieldLabel>
      <FormFieldControl>
        <DropdownMultiselect.Root
          disabled={disabled}
          placeholder={placeholder}
          {...multiselectDropdownProps}
        >
          <DropdownMultiselect.Selection>
            {options.map(({ value, name }) => (
              <DropdownMultiselect.SelectedValue key={value} value={value}>
                {name}
              </DropdownMultiselect.SelectedValue>
            ))}
          </DropdownMultiselect.Selection>
          <DropdownMultiselect.Popover title={label} closeText={intl.formatMessage({ id: 'close' })}>
            <FocusPadding>
              {options.map(({ value, name, resultsCount }) => (
                <DropdownMultiselect.CheckboxWithSupportingTextOption key={value} value={value} supportingText={resultsCount?.toString()}>
                  {name}
                </DropdownMultiselect.CheckboxWithSupportingTextOption>
              ))}
            </FocusPadding>
            <DropdownMultiselect.ConfirmButton>
              {confirmButton}
            </DropdownMultiselect.ConfirmButton>
          </DropdownMultiselect.Popover>
        </DropdownMultiselect.Root>
      </FormFieldControl>
    </FormField>
  )
}

export function mapLocalSelectionToGA4Result(localSelection: string | string[] | undefined, options: Option[]) {
  if (!localSelection)
    return ''
  return options
    .filter(option => localSelection.includes(option.value))
    .map(option => option.name)
    .join(',')
}

export default MultiSelectPricesFilter
